import React, { useState, useEffect, useContext } from 'react';

import Button from '../../@base/Button';
import Checkbox from '../../@base/Checkbox';
import Datatable from '../../@base/DataTable';
import Input from '../../@base/Input';
import WatsonContext from '../../../context/WatsonContext';
import { Validation } from '../../BotComponents/Commons';
import { validateIntentsAndEntitiesName, showRemoveConfirmation } from '../Commons';

import style from './Entities.module.css';
import { EntityDetailActions } from './EntityDetailAddValueButton';

const EntityDetail = (props) => {
  const watsonContext = useContext(WatsonContext);
  const [isValidEntity, setIsValidEntity] = useState(true);
  const [valueName, setValueName] = useState('');
  const [valueSynonyms, setValueSynonyms] = useState([]);
  const [selectedValuesToDelete, setSelectedValuesToDelete] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState({
    id: null,
    name: '',
    newName: '',
    description: '',
    values: [],
    fuzzyMatch: true,
  });

  /** Volta para visão de lista de entidades */
  const backToListView = () => {
    props.closeEntityDetail();
  };

  /** Altera entidade selecionada depois do salvamento no contexto do Watson */
  const postSaveEntity = (e) => {
    setSelectedEntity({
      ...e,
      newName: e.name,
    });
  };

  /** Mostra migalha de pão para detalhamento de entidade */
  const showBreadsrumb = () => {
    const breadsCrumb = selectedEntity.name === ''
      ? 'Nova entidade'
      : `Entidade - ${selectedEntity.name}`;

    return <div className={style.Title}>{breadsCrumb}</div>;
  };

  /** Cria entidade no contexto do Watson se a intenção for valida */
  const createEntity = () => {
    if (isValidEntity) {
      const newEntity = { ...selectedEntity, name: selectedEntity.newName };
      watsonContext.saveIntentOrEntity('entities', newEntity, true, postSaveEntity);
    }
  };

  /** Atualiza entidade no contexto do Watson se a intenção for valida */
  const updateEntity = () => {
    const isCreate = !selectedEntity.id;
    if (isValidEntity && !isCreate) {
      watsonContext.saveIntentOrEntity('entities', selectedEntity, isCreate, postSaveEntity);
    }
  };

  /** Manipulador da ação de validação do nome da intent */
  const setNameInvalidHandler = () => {
    let notAllowedNames = Object.values(watsonContext.entities);
    if (selectedEntity.id) {
      notAllowedNames = notAllowedNames.filter((i) => i.id !== selectedEntity.id);
    }

    const validator = validateIntentsAndEntitiesName(selectedEntity.newName, notAllowedNames);
    return (validator);
  };

  /** Mostra conjutos de campos principais para detalhamento de entidade */
  const showMainFieldSet = () => (
    <>
      <Button small circle classes="float-left" onClick={backToListView}>
        <small><i className="fa fa-angle-left" aria-hidden="true" /></small>
      </Button>
      {showBreadsrumb()}
      <div className="clearfix" />
      <Validation
        isValid={setIsValidEntity}
        setInvalid={setNameInvalidHandler}
      >
        <Input
          label="Nome:"
          small
          classes="mt-2"
          value={selectedEntity.newName}
          mandatory
          onChange={(e) => {
            setSelectedEntity({ ...selectedEntity, newName: e.target.value });
          }}
          onBlur={updateEntity}
        />
      </Validation>
      <Input
        label="Descrição:"
        small
        classes="mt-2"
        value={selectedEntity.description}
        onChange={(e) => setSelectedEntity({ ...selectedEntity, description: e.target.value })}
        onBlur={updateEntity}
      />

      {!selectedEntity.id ? (
        <Button
          primary
          small
          classes="mt-2 float-right"
          onClick={createEntity}
          disabled={!isValidEntity}
        >
          <small>Criar Entidade</small>
        </Button>
      ) : null}
    </>
  );

  /** Mostra linhas de valores da entidade */
  const showValues = () => {
    if (selectedEntity.id) {
      const lines = selectedEntity.values.map((x, i) => {
        const { value } = x;
        let { synonyms } = x;
        synonyms = synonyms || [];
        synonyms = synonyms.join(', ');
        return (
          <tr key={value}>
            <td>
              <Checkbox id={`value_${i}`} />
            </td>
            <td data-th="Valor">{value}</td>
            <td data-th="Sinônimos">{synonyms}</td>
          </tr>
        );
      });

      return (<tbody>{lines}</tbody>);
    }

    return <></>;
  };

  /** Cria valor de entidade no contexto do watson */
  const saveValues = () => {
    setValueName('');
    setValueSynonyms([]);

    const valuesName = selectedEntity.values.map((e) => e.value);
    if (valuesName.includes(valueName)) return null;

    const newValue = {
      value: valueName,
      type: 'synonyms',
      synonyms: Object.values(valueSynonyms).map((x) => x.text),
      pattern: null,
    };

    const entityChanged = {
      ...selectedEntity,
      values: selectedEntity.values.concat(newValue),
    };

    setSelectedEntity(entityChanged);

    if (isValidEntity) {
      watsonContext.saveIntentOrEntity('entities', entityChanged, false, (e) => postSaveEntity(e));
    }
  };

  /** Manipula botão de deletar valores da entidade */
  const deleteValuesHandler = (values) => {
    const valuesToRemove = [].concat(values);
    if (valuesToRemove.length > 0) setSelectedValuesToDelete(valuesToRemove);
  };

  /** Mostra conjuntos de campos para lista de valores da entidade */
  const showValuesListFieldSet = () => {
    if (!selectedEntity.id) return <></>;

    let className = style.Values;
    if (selectedEntity.values.length === 0) className = `${className} ${style.NoValues}`;

    const actions = (
      <EntityDetailActions
        valueName={valueName}
        setValueName={setValueName}
        valueSynonyms={valueSynonyms}
        setValueSynonyms={setValueSynonyms}
        saveValues={saveValues}
      />
    );

    return (
      <div className={className}>
        <Datatable
          actionsItems={actions}
          classes={style.DatatableValues}
          deleteRows={deleteValuesHandler}
        >
          <table>
            <thead>
              <tr>
                <th scope="col" style={{ width: 60 }}>
                  <Checkbox id="entityvalue_check_all" />
                </th>
                <th scope="col" style={{ width: 100 }}>
                  Valor
                </th>
                <th scope="col">Sinônimos</th>
              </tr>
            </thead>
            {showValues()}
          </table>
        </Datatable>
      </div>
    );
  };

  /** Remove valores de entidade no contexto do watson */
  const removeValues = (valuesToDelete) => {
    const changedEntity = {
      ...selectedEntity,
      values: selectedEntity.values.filter((e, i) => !valuesToDelete.includes(`value_${i}`)),
    };
    setSelectedEntity(changedEntity);
    if (isValidEntity) {
      watsonContext.saveIntentOrEntity('entities', changedEntity, false, () => setSelectedValuesToDelete([]));
    }
  };

  /** Efeito executado na montagem do componente
   *  Carrega estado selectedEntity */
  useEffect(() => {
    if (props.selectedEntity) {
      setSelectedEntity({ ...props.selectedEntity, newName: props.selectedEntity.name });
    }
  }, []);

  return (
    <>
      {showRemoveConfirmation(
        selectedValuesToDelete,
        removeValues,
        setSelectedValuesToDelete,
      )}
      <div className={style.EntityDetail}>
        {showMainFieldSet()}
        <div className="clearfix" />
        {showValuesListFieldSet()}
      </div>

    </>
  );
};

export default EntityDetail;
